import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import {
  CalendarCheck,
  FileText,
  MessageSquare,
  Phone,
  PhoneMissed,
  RotateCcw,
  Share2,
} from 'lucide-react';

import { cn } from '../../../lib/utils';

// "They came back" (Nic 2026-10-15): what a nurtured or dropped person did,
// in plain words, for the reached-out rows on the Nurture and Dropped tabs.
// `reachOut` is the server's { type, at, preview, answered, startTime,
// partnerName, referredBy } (lib/leadReachOut.js latestOpenReachOuts).
export const reachOutSummary = (reachOut) => {
  if (!reachOut) return null;
  const { type, preview, answered, startTime, partnerName, referredBy } = reachOut;
  switch (type) {
    case 'cadence_text_detected':
      return { Icon: MessageSquare, text: preview ? `Texted "${preview}"` : 'Texted us' };
    case 'cadence_call_detected':
      return answered === false
        ? { Icon: PhoneMissed, text: 'Called, we missed it' }
        : { Icon: Phone, text: 'Called us' };
    case 'calendly_booking_detected':
      return {
        Icon: CalendarCheck,
        text: startTime
          ? `Booked a meeting for ${moment(startTime).format('ddd MMM D, h:mm A')}`
          : 'Booked a meeting',
      };
    case 'reach_out_reengaged':
      return { Icon: FileText, text: 'Filled in the website form again' };
    case 'event_reengaged':
      return {
        Icon: FileText,
        text: partnerName ? `Signed up again at ${partnerName}` : 'Signed up again at an event',
      };
    case 'partner_qr_reengaged':
      return {
        Icon: FileText,
        text: partnerName ? `Used ${partnerName}'s page again` : "Used a partner's page again",
      };
    case 'referral_reengaged':
      return { Icon: Share2, text: referredBy ? `Referred again by ${referredBy}` : 'Referred again' };
    case 'partner_lead_reengaged':
      return { Icon: Share2, text: `Sent again by ${partnerName || 'a partner'}` };
    default:
      return { Icon: MessageSquare, text: 'Reached out' };
  }
};

// One line under the name: what came in. A text's words read in full on hover.
// Times re-engaged (Nurture and Dropped rows): the count, and where each
// came back from on hover (Nic 2026-10-15: "Brought back from Nurture 2
// times" was wrong for someone re-engaged from Dropped twice).
export const ReengagedBadge = ({ r }) => {
  const n = r.reengagedCount || 0;
  if (!n) return <span className="text-[13px] text-slate-300 dark:text-slate-600">-</span>;
  const parts = [
    r.reengagedFromNurture ? `${r.reengagedFromNurture} from Nurture` : null,
    r.reengagedFromDropped ? `${r.reengagedFromDropped} from Dropped` : null,
  ].filter(Boolean);
  const title = `Re-engaged ${n} time${n === 1 ? '' : 's'}${
    parts.length ? ` (${parts.join(', ')})` : ''
  }${r.lastReengagedAt ? `, last on ${moment(r.lastReengagedAt).format('MMM D')}` : ''}`;
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-full px-2 text-xs font-semibold tabular-nums',
        n >= 3
          ? 'bg-amber-100 text-amber-800 dark:!bg-amber-400/15 dark:text-amber-300'
          : 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
      )}
    >
      <RotateCcw size={11} /> {n}
    </span>
  );
};

export const ReachOutLine = ({ reachOut, className = '' }) => {
  const s = reachOutSummary(reachOut);
  if (!s) return null;
  const { Icon, text } = s;
  return (
    <div
      title={text}
      className={cn(
        'mt-1 flex min-w-0 items-center gap-1.5 text-xs text-sky-800 dark:text-sky-200',
        className,
      )}
    >
      <Icon size={12} className="shrink-0 text-sky-600 dark:text-sky-400" />
      <span className="min-w-0 truncate">{text}</span>
    </div>
  );
};

// A small centered dialog (Leave with an optional note, or a confirm). Esc
// and a click outside close it; Enter in the note confirms.
export const ReachOutDialog = ({
  open,
  title,
  children,
  noteLabel = null,
  notePlaceholder = '',
  confirmLabel,
  tone = 'slate',
  busy = false,
  // Not until something's picked (the re-engage "Who gets them?").
  confirmDisabled = false,
  onConfirm,
  onClose,
}) => {
  const [note, setNote] = useState('');
  // The note clears only when the dialog opens (QA 2026-10-26): callers pass
  // a fresh onClose on every render and the board's clock re-renders each
  // second, which wiped what the rep was typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white px-5 pb-4 pt-4 shadow-[0_20px_60px_rgba(15,23,42,0.3)] dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10"
      >
        <h3 className="mb-1.5 text-base font-bold text-slate-800 dark:text-slate-100">{title}</h3>
        <div className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{children}</div>
        {noteLabel ? (
          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              {noteLabel}
            </span>
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={note}
              maxLength={200}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !busy && !confirmDisabled) onConfirm(note.trim());
              }}
              placeholder={notePlaceholder}
              className="w-full rounded-lg bg-white px-3 py-2 text-sm text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-slate-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10"
            />
          </label>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 outline-none hover:bg-slate-100 focus:outline-none dark:text-slate-300 dark:hover:!bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || confirmDisabled}
            onClick={() => onConfirm(note.trim())}
            className={cn(
              'rounded-xl px-4 py-2 text-sm font-semibold text-white outline-none transition-[background-color] focus:outline-none disabled:opacity-50',
              tone === 'rose'
                ? 'bg-rose-600 hover:bg-rose-500'
                : tone === 'sky'
                  ? 'bg-sky-600 hover:bg-sky-500'
                  : 'bg-slate-900 hover:bg-slate-700 dark:!bg-slate-100 dark:!text-slate-900',
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
